/**
 * The shared shape of an operator list page.
 *
 * All three lists (clients, cohorts, students) had their own header, their
 * own search box, and their own row of filters wrapping across the top. One
 * layout instead, so the next list added inherits it rather than inventing a
 * fourth arrangement.
 *
 * ```
 * ┌────────────────────────────────────────────────┐
 * │ Title + count                       [Action]  │  sticky shell header
 * ├──────────────────────────────────────────────┤
 * │ [ search, full width ]                       │  sticky list toolbar
 * ├────────────────────────────────┬─────────────┤
 * │ table                            │ Filters     │  body
 * │                                  │  (rail)     │
 * ├──────────────────────────────────┴─────────────┤
 * │ result count               [Previous] [Next] │  fixed pagination tier
 * └────────────────────────────────────────────────┘
 * ```
 *
 * **Search stays in the sticky list toolbar; filters move to a rail on the right.**
 * They are different actions: search is the thing an operator reaches for
 * first and types into immediately, so it wants to be prominent and wide.
 * Filters are set once and left alone, and stacking them in a rail stops a
 * growing set from pushing the table further down the page every time one is
 * added — which is what a wrapping filter row does.
 *
 * The rail is OPTIONAL. A list with nothing to filter (clients, today)
 * renders full width rather than showing an empty rail, because an empty
 * panel reads as something that failed to load.
 *
 * DOM order is table-then-rail, so the narrow-screen stack puts the content
 * first. This is a desktop internal tool; if it ever needs to be usable on a
 * phone, the rail wants `order` classes rather than a DOM reshuffle.
 */
import { useAtom, useAtomValue } from 'jotai'
import type { ReactNode } from 'react'
import {
  ListFilterRail as SharedListFilterRail,
  ListPaginationTier as SharedListPaginationTier,
  ListSearchTier,
} from '@speaking-coach/roster-list'
import {
  Button,
  buttonVariants,
  FieldHelp,
  Icon,
  Input,
  InputGroup,
  InputGroupAddon,
  type InputProps,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
} from '@speaking-coach/ui'
import { PageHeader } from '../components/page-header'
import {
  filtersCollapsedAtom,
  fullWidthAtom,
  listContainerClass,
  sidebarCollapsedAtom,
} from '../state/layout'

export function ListPage({
  title,
  count,
  action,
  search,
  filters,
  filterAction,
  activeFilterCount = 0,
  pagination,
  children,
}: {
  title: ReactNode
  /**
   * How many entries the list currently holds, shown as a badge beside the
   * title. The data is already loaded, so the number is free — and it answers
   * "how big is this?" without making an operator read the pager.
   *
   * Pass `undefined` while loading: a badge reading `0` before the first
   * response is a wrong answer, not a pending one.
   *
   * It reflects the ACTIVE FILTERS, which is the same thing the table shows.
   * The tooltip says so, because `Students 3` next to a filtered list would
   * otherwise read as "this system has three students".
   */
  count?: number
  /** The primary action, e.g. "New student". Top-right in the shell header. */
  action?: ReactNode
  /**
   * The search input. Full width in the subheader. Omit ONLY when the
   * endpoint offers no text search at all (the activity feed): a search
   * box that filters nothing server-side is a promise the list cannot
   * keep, and filtering the fetched page client-side is the bug the
   * tables section exists to prevent. The toolbar tier still renders,
   * because the width preference lives there.
   */
  search?: ReactNode
  /** Filter controls, stacked in the right rail. Omit for no rail. */
  filters?: ReactNode
  /** Optional reset action beside the Filters title. */
  filterAction?: ReactNode
  /**
   * How many of the RAIL's filters are currently set — the badge on the
   * collapsed rail's button, so a hidden rail cannot silently filter the
   * table. Excludes the search term, which stays visible either way.
   */
  activeFilterCount?: number
  /**
   * Pagination for a long list. Rendered in a fixed bottom tier so operators
   * can change pages without walking to the end of a 50-row table.
   */
  pagination?: ReactNode
  /** The table body. */
  children: ReactNode
}): JSX.Element {
  // Wide tables want the whole viewport; forms do not. The operator chooses
  // once and it sticks — see `state/layout.ts`.
  const [fullWidth, setFullWidth] = useAtom(fullWidthAtom)
  // Same contract for the rail: collapsing trades it for ~16rem of columns.
  const [filtersCollapsed, setFiltersCollapsed] = useAtom(filtersCollapsedAtom)
  // `100svh-3.5rem`, not `min-h-screen`: the 56px shell bar sits ABOVE
  // this main in flow, so a full-viewport main made every page
  // scrollable by 56px even when its content fit — most visible on an
  // empty list, where nothing justifies a scrollbar at all.
  return (
    <main
      data-slot="list-page"
      className={`min-h-[calc(100svh-3.5rem)] bg-surface-app px-8 ${pagination === undefined ? 'pb-8' : 'pb-20'}`}
    >
      <div className={listContainerClass(fullWidth)}>
        <PageHeader
          title={title}
          count={count}
          action={action}
        />
      </div>

      {/* This second sticky tier keeps global discovery available below the
          page-identity bar while an operator works down a long table. Its
          surface and divider run edge-to-edge across the content pane; the
          inner controls keep the same width and alignment as the table.
          There is deliberately no page-top padding before it: its resting
          and stuck positions use the same spacing beneath the shell header.
          The width control sits WITH the table it affects, not in the nav.
          It only applies to list pages, so a global control was a promise
          the form pages could not keep. */}
      <ListSearchTier
        className="sticky top-14 z-20 -mx-8 mb-4 border-b border-border-default bg-surface-app px-8 py-2"
        contentClassName={`${listContainerClass(fullWidth)} flex min-h-10 items-center gap-4`}
        trailing={
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <Switch
              id="list-full-width"
              tone="neutral"
              checked={fullWidth}
              onCheckedChange={setFullWidth}
            />
            <label htmlFor="list-full-width" className="text-body text-text-secondary">
              Full width
            </label>
          </div>
        }
      >
        {/* `min-h-10`: the tier is sized by the search INPUT everywhere
            else; a page whose slot holds a text line (History, the
            Activity feed identity) or nothing must not sit 20px higher —
            the rail and the collapsed button anchor to `top-32` assuming
            this height. */}
        {search}
      </ListSearchTier>

      <div className={listContainerClass(fullWidth)}>
        {filters === undefined ? (
          children
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:min-h-[calc(100svh-13rem)]">
            <div className="w-full flex-1 min-w-0">{children}</div>
            <SharedListFilterRail
              collapsed={filtersCollapsed}
              activeFilterCount={activeFilterCount}
              title="Filters"
              headerAction={filterAction}
              showLabel={
                activeFilterCount > 0
                  ? `Show filters (${activeFilterCount} active)`
                  : 'Show filters'
              }
              hideLabel="Hide filters"
              onCollapsedChange={setFiltersCollapsed}
              classes={{
                collapsedButton:
                  buttonVariants({ size: 'iconLg' }) +
                  ' relative shrink-0 bg-surface-card lg:sticky lg:top-32',
                activeBadge:
                  'absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-label font-medium leading-none text-on-accent tabular-nums',
                rail:
                  'w-full shrink-0 space-y-4 rounded-token-card bg-surface-card p-4 lg:sticky lg:top-32 lg:max-h-[calc(100svh-13rem)] lg:w-64 lg:overflow-y-auto',
                heading: 'flex items-center gap-2',
                title: 'flex items-center gap-1.5 font-medium text-body',
                hideButton: buttonVariants({ variant: 'ghost', size: 'icon' }) + ' ml-auto',
              }}
            >
              {filters}
            </SharedListFilterRail>
          </div>
        )}
      </div>

      {pagination !== undefined && (
        <PaginationTier container={listContainerClass(fullWidth)}>
          {filters === undefined ? (
            pagination
          ) : (
            <div className="flex items-center gap-6">
              <div className="min-w-0 flex-1">{pagination}</div>
              {/* Keep the pager aligned with the table rather than the
                  complete table + filter layout. */}
              <div
                aria-hidden
                data-slot="list-pagination-filter-spacer"
                className={`hidden shrink-0 lg:block ${filtersCollapsed ? 'w-9' : 'w-64'}`}
              />
            </div>
          )}
        </PaginationTier>
      )}
    </main>
  )
}

/**
 * The fixed bottom pagination tier — one shape wherever a paged view
 * lives, list page or a detail page's History mode.
 *
 * Fixed, rather than sticky: a sticky element placed after a long table
 * only becomes visible once the operator has already reached it. The
 * page reserves its height above (`pb-20` / a trailing spacer), so it
 * never covers the last row. The tier owns the full-width divider and
 * distinct card surface; only its contents inherit the caller's
 * container width, and its left edge follows the persisted sidebar
 * width like the rest of the content pane.
 */
export function PaginationTier({
  container,
  children,
}: {
  /** Width class for the tier's contents — align with the table it pages. */
  container: string
  children: ReactNode
}): JSX.Element {
  const sidebarCollapsed = useAtomValue(sidebarCollapsedAtom)
  return (
    <SharedListPaginationTier
      className={`fixed bottom-0 left-0 right-0 z-30 border-t border-border-default bg-surface-card px-8 py-2 transition-[left] duration-200 motion-reduce:transition-none ${sidebarCollapsed ? 'lg:left-16' : 'lg:left-60'}`}
      contentClassName={container}
    >
      {children}
    </SharedListPaginationTier>
  )
}

/**
 * One pagination control for every top-level Admin list.
 *
 * Page changes return to the result start. Keeping the viewport halfway down
 * while replacing every row makes the new page look like a continuation of
 * the old one and hides its first results.
 */
export function ListPagination({
  summary,
  page,
  pageCount,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
}: {
  summary: ReactNode
  /** Zero-based page index. Omit with `pageCount` when the API exposes only hasMore. */
  page?: number
  pageCount?: number
  previousDisabled: boolean
  nextDisabled: boolean
  onPrevious: () => void
  onNext: () => void
}): JSX.Element {
  function changePage(change: () => void): void {
    change()
    window.scrollTo(0, 0)
  }

  return (
    <nav
      aria-label="Pagination"
      className="flex min-h-9 items-center justify-between gap-4 text-caption text-text-secondary"
    >
      <span>{summary}</span>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="text-caption"
          disabled={previousDisabled}
          onClick={() => changePage(onPrevious)}
        >
          Previous
        </Button>
        {page !== undefined && pageCount !== undefined && (
          <span className="tabular-nums">
            Page {page + 1} / {pageCount}
          </span>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="text-caption"
          disabled={nextDisabled}
          onClick={() => changePage(onNext)}
        >
          Next
        </Button>
      </div>
    </nav>
  )
}

/**
 * The subheader search box — one shape for every list.
 *
 * All three lists carried an identical sr-only-label + `<Input type="search">`
 * block; the leading magnifier made it a fourth copy waiting to drift, so the
 * block moved here instead. The label stays sr-only (the icon and the
 * placeholder say "search" visually), and the icon is decorative — the
 * accessible name comes from the label.
 */
export function SearchField({
  id,
  label,
  ...inputProps
}: InputProps & {
  id: string
  /** The sr-only label, e.g. "Search students". */
  label: string
}): JSX.Element {
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <InputGroup>
        <InputGroupAddon>
          <Icon name="search" />
        </InputGroupAddon>
        {/* `pl-9` clears the addon — explicit here by the input-group
            contract, so the input declares its own layout. `raised` is
            the DEFAULT here, not at each call site: this field only
            ever lives on the toolbar tier's app-canvas background,
            and the one page that forgot the prop shipped a
            gray-on-gray search box. */}
        <Input
          id={id}
          type="search"
          className="pl-9"
          surface="raised"
          {...inputProps}
        />
      </InputGroup>
    </>
  )
}

/**
 * A labeled control in the filter rail.
 *
 * Exists so every filter in every list gets the same label treatment — the
 * rail is a stack of these, and without a wrapper each list drifts into its
 * own spacing and label size.
 */
export function FilterField({
  label,
  htmlFor,
  children,
  help,
  active = false,
}: {
  label: string
  htmlFor?: string
  children: ReactNode
  /**
   * Rendered as a `FieldHelp` popover beside the label, not inline
   * prose under the control: a rail is a stack of filters, and a
   * sentence under each one doubles its height for guidance an
   * operator reads once. Same shape as form-label help, so "the ？
   * explains it" is one learned gesture across the app.
   */
  help?: string
  /** Whether this field currently constrains the results. */
  active?: boolean
}): JSX.Element {
  return (
    <div className={active ? '[&_input]:border-accent [&_input]:bg-accent-soft [&_[role=combobox]]:border-accent [&_[role=combobox]]:bg-accent-soft' : undefined}>
      <span className="mb-1 flex items-center gap-1">
        <label htmlFor={htmlFor} className="block text-body">
          {label}
        </label>
        {help !== undefined && (
          <FieldHelp label={label} size="compact">
            {help}
          </FieldHelp>
        )}
        {active && <span role="img" aria-label="Applied filter" className="ml-auto mr-2 size-1.5 shrink-0 rounded-full bg-text-primary" />}
      </span>
      {children}
    </div>
  )
}

/**
 * The rail's reset control.
 *
 * Trivial on its own; extracted because three lists render it and the variant
 * and size are the part that drifts. Callers own what "clear" means — every
 * list keeps a different set of filters, and at least one of them (the search
 * box) always lives outside the rail.
 */
export function ClearFiltersButton({
  hasFilters,
  onClear,
}: {
  /**
   * The SAME predicate the empty state uses. The button renders only
   * while something is actually set: a permanently visible "Clear
   * filters" reads as "something is filtering this table" — and
   * clicking it then changes nothing, which reads as broken.
   */
  hasFilters: boolean
  onClear: () => void
}): JSX.Element | null {
  if (!hasFilters) return null
  return (
    <Tooltip label="Clear filters">
      <Button
        type="button"
        aria-label="Clear filters"
        onClick={onClear}
        variant="secondary"
        size="iconSm"
      >
        <Icon name="clearFilters" size={14} />
      </Button>
    </Tooltip>
  )
}

/**
 * What a list shows when it has nothing to show.
 *
 * Two genuinely different states behind one component, because the difference
 * is what an operator should DO. "No rows at all" is a prompt to create the
 * first one; "no rows matching" is a prompt to widen the filters, and it is
 * the one that used to dead-end — the message named the cause while the remedy
 * sat in the rail on the other side of the page.
 *
 * The action is labeled by its OUTCOME ("Show all students") rather than
 * repeating the rail's "Clear filters". Two controls with the same accessible
 * name on one screen is ambiguous to a screen reader, and naming the result is
 * the more useful sentence in this position anyway.
 */
export function FilteredEmptyState({
  hasFilters,
  noun,
  onClear,
  emptyHint,
}: {
  hasFilters: boolean
  /** Plural, lowercase — "students", "clients", "cohorts". */
  noun: string
  onClear: () => void
  /** Shown when nothing is filtered: how to create the first row. */
  emptyHint: string
}): JSX.Element {
  if (!hasFilters) {
    return <p className="text-text-secondary">{emptyHint}</p>
  }
  return (
    <div className="text-text-secondary space-y-2">
      <p>No {noun} match your filters.</p>
      <Button type="button" onClick={onClear} variant="secondary" size="sm">
        Show all {noun}
      </Button>
    </div>
  )
}

/**
 * The `is_internal` filter, three-state.
 *
 * The value is a raw string, not a boolean, and that is the whole reason this
 * is shared: `''` (don't filter) and `'false'` (customers only) are different
 * requests, and a boolean cannot hold the distinction. Every call site has to
 * convert the same way on the way out, so the conversion lives here too —
 * see `classificationParam`.
 *
 * The labels are identical across lists on purpose. "Internal" means one thing
 * (excluded from customer analytics, never an access rule) and a list that
 * worded it differently would imply a second meaning.
 */
export function ClassificationFilter({
  id,
  value,
  onChange,
  help,
}: {
  id: string
  value: string
  onChange: (next: string) => void
  help: string
}): JSX.Element {
  return (
    <FilterField active={value !== ''} label="Classification" htmlFor={id} help={help}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} size="sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">Everyone</SelectItem>
          <SelectItem value="false">Customers only</SelectItem>
          <SelectItem value="true">Internal only</SelectItem>
        </SelectContent>
      </Select>
    </FilterField>
  )
}

/**
 * The raw filter string as the API wants it: `undefined` to not filter at all,
 * which is NOT the same as `false`. A truthiness test here is the bug this
 * function exists to stop being written three times.
 */
export function classificationParam(value: string): boolean | undefined {
  return value === '' ? undefined : value === 'true'
}
